import { TableHead } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * A column header that says what the column is and how it ranks.
 *
 * The same words wherever a metric is tabulated -- leaderboard, a student's attempts, a
 * TA's list -- since a student reading "SPL 0.68" on one page and on another should get
 * one explanation. The header is the trigger: dotted, the way a defined term is marked,
 * rather than an icon beside it that only some columns would carry.
 */
export type Metric =
  | "goal"
  | "result"
  | "score"
  | "steps"
  | "spl"
  | "distance"
  | "heading"
  | "time";

const ABOUT: Record<Metric, { label: string; about: string }> = {
  goal: {
    label: "Goal",
    about:
      "Whether the run arrived, by the rubric's goal condition, and how well: Perfect is the Distance block's first rule, Partial any other. Missed scores nothing.",
  },
  result: {
    label: "Result",
    about:
      "How the attempt ended. Once checked in: Perfect, Partial or Missed under the rubric. Aborted, Expired and Failed ended without a check-in and score nothing, but each spent an attempt.",
  },
  score: {
    label: "Score",
    about:
      "Points under this challenge's rubric, out of what an ideal run would earn: a miss scores nothing; within each block the first matching rule pays; the blocks add up. Ranks first.",
  },
  steps: {
    label: "Steps",
    about:
      "Simulation steps the run used -- one per tick of moving or turning. What SPL measures the shortest route against; where a challenge has no SPL (an upload), fewer wins a tie on points.",
  },
  spl: {
    label: "SPL",
    about:
      "Success weighted by path length: the shortest route's steps, turns included, divided by yours, capped at 1. 1 means you took the shortest route; 0 means you missed the goal. Breaks a tie on points.",
  },
  distance: {
    label: "Distance",
    about:
      "Metres from the target at check-in. Shown once a run reaches the goal; a miss is not told how far off it was. The last tiebreak.",
  },
  heading: {
    label: "Heading",
    about:
      "Degrees between your heading at check-in and target view 0, the way to arrive facing. Shown once a run reaches the goal.",
  },
  time: {
    label: "Time",
    about:
      "Wall clock from the first step to the last. Shown and never ranked: it measures the machine and the network, not the navigation.",
  },
};

export function MetricHead({ metric, className }: { metric: Metric; className?: string }) {
  const { label, about } = ABOUT[metric];
  return (
    <TableHead className={className}>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              className={cn(
                "cursor-help rounded-sm underline decoration-dotted underline-offset-4",
                "decoration-muted-foreground/50 hover:decoration-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              )}
            />
          }
        >
          {label}
        </TooltipTrigger>
        <TooltipContent className="max-w-72 text-left">{about}</TooltipContent>
      </Tooltip>
    </TableHead>
  );
}
